import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Section from 'components/base/Section';
import { useRef } from 'react';
// @ts-expect-error no types available
import html2pdf from 'html2pdf.js';
import React, { useState } from 'react';
import logo from 'assets/img/icons/logo-light-multi.png';
import { useParams } from 'react-router-dom';

import { faBagShopping, faFloppyDisk } from '@fortawesome/free-solid-svg-icons';

import {
  useGenerateStandardizedProjectJsonMutation,
  useGenerateAuditReportMutation,
  useGetProjectResultsQuery
} from '../project-details/data-access/project.api';

import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import AuditTable from '../../../components/tables/AuditTable';

import ModelScoresByType from './ModelScoresByType';
import { useSelector } from 'react-redux';
import { selectProjectResults } from '../project-details/data-access/project.reducer';
import AuditSummary from '../../../components/modules/project-management/project-details/AuditSummary';
import AuditFinalDecision from '../../../components/cards/AuditFinalDecision';
import { Col, Row } from 'react-bootstrap';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  textAlign: 'center',
  minWidth: 300
};

const AuditReport = () => {
  const { projectId } = useParams<{ projectId?: string }>();
  const reportRef = useRef<HTMLDivElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [statusText, setStatusText] = useState('Standardizing data...');
  const [isSaving, setIsSaving] = useState(false);

  const [generateStdJson] = useGenerateStandardizedProjectJsonMutation();

  const [generateAuditReport] = useGenerateAuditReportMutation();
  const { refetch } = useGetProjectResultsQuery(projectId ?? '', {
    skip: !projectId
  });

  const handleStandardizeReport = async () => {
    if (!projectId) return;

    const userId = localStorage.getItem('userId');
    if (!userId) {
      setStatusText('User not found. Please log in again.');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 2000);
      return;
    }

    setStatusText('Standardizing data...');
    setShowModal(true);

    try {
      await generateStdJson({ projectId, userId }).unwrap();

      setStatusText('Generating audit report...');

      await generateAuditReport({ projectId, userId }).unwrap();
      await refetch(); // ✅ Fetch latest results
      setShowModal(false); // ✅ Success
    } catch (err) {
      setStatusText('Something went wrong. Please try again.');
      setTimeout(() => setShowModal(false), 2000);
    }
  };

  const handlePrintPDF = () => {
    if (!reportRef.current) return;

    setIsSaving(true); // Start spinner

    const originalStyle = reportRef.current.style.transform;
    reportRef.current.style.transform = 'scale(1)';
    reportRef.current.style.transformOrigin = 'top left';

    const opt = {
      margin: 0,
      filename: `Audit_Report_${projectId}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: {
        scale: 3, // 10 is too heavy for multi-page layout
        useCORS: true
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
    };

    setTimeout(() => {
      html2pdf()
        .set(opt)
        .from(reportRef.current)
        .save()
        .then(() => {
          reportRef.current!.style.transform = originalStyle;
        })
        .finally(() => {
          setIsSaving(false); // Stop spinner
        });
    }, 100);
  };

  const projectResults = useSelector(selectProjectResults);

  const { results } = projectResults;
  return (
    <div className="pt-5 pb-9 bg-body-emphasis dark__bg-gray-1200 border-top">
      <Section small className="py-0">
        <div
          className="d-flex justify-content-between align-items-center mt-4"
          style={{ padding: 24 }}
        >
          {!results.audit_report ? (
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={handleStandardizeReport}
            >
              <FontAwesomeIcon icon={faBagShopping} />
              Generate Audit Report
            </button>
          ) : (
            <>
              <div>
                <button
                  className="btn btn-phoenix-secondary d-flex align-items-center gap-2"
                  onClick={handlePrintPDF}
                  disabled={isSaving}
                >
                  <FontAwesomeIcon icon={faFloppyDisk} className="me-sm-2" />
                  {isSaving ? (
                    <>
                      Saving...
                      <CircularProgress
                        size={16}
                        sx={{ color: '#000', ml: 1 }}
                        thickness={5}
                      />
                    </>
                  ) : (
                    <span className="d-none d-sm-inline-block">Save</span>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {results && results.audit_report && (
          <div
            ref={reportRef}
            style={{
              padding: 24,
              background: '#fff',
              color: '#000',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            <Row>
              <Col xs={10}>
                <h2 className="text-body-emphasis fw-bolder mb-2">
                  {results.audit_report['Report Header']}
                </h2>
                <AuditSummary
                  reviewer={results.audit_report['Reviewer']}
                  auditDate={results.audit_report['Audit Date']}
                  projectId={results.audit_report['Project ID']}
                  projectName={results.audit_report['Project Name']}
                />
              </Col>
              <Col xs={2}>
                <img src={logo} alt={'Microeai-logo'} style={{ width: 150 }} />
              </Col>
            </Row>

            <h4 className={'mt-5 mb-3'}>Compliance Data</h4>
            <AuditTable data={results.audit_report['Compliance Data']} />
            <div className={'mt-5'}>
              <ModelScoresByType
                scores={results.audit_report['Model Scores by Type']}
              />
            </div>
            <div>
              <h4 className={'mt-5 mb-3'}>Risk Data</h4>
              <AuditTable data={results.audit_report['Risk Data']} />
            </div>
            <div>
              <h4 className={'mt-5 mb-3'}>Audit Final Decision</h4>
              <h5 className={'mt-5 mb-3'}>LLM</h5>
              <AuditFinalDecision
                auditData={results.audit_report['Final Audit Decision'].LLM}
              />
              <h5 className={'mt-5 mb-3'}>ML</h5>
              <AuditFinalDecision
                auditData={results.audit_report['Final Audit Decision'].ML}
              />
            </div>
          </div>
        )}
      </Section>

      {/* ✅ Loader Modal */}
      <Modal open={showModal} onClose={() => {}} disableEscapeKeyDown>
        <Box sx={modalStyle}>
          <CircularProgress sx={{ mb: 2 }} />
          <p className="fw-semibold">{statusText}</p>
        </Box>
      </Modal>
    </div>
  );
};

export default AuditReport;
